<template>
  <div class="flex flex-col gap-2" data-cy="call-transcript">
    <div class="flex items-center gap-2">
      <div class="relative flex-1">
        <i
          class="fa-regular fa-magnifying-glass absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
        ></i>
        <input
          v-model="query"
          type="search"
          class="form-control pl-7"
          placeholder="Search transcript"
          aria-label="Search transcript"
          data-cy="call-transcript-search"
        />
      </div>
      <span
        v-if="searching"
        class="text-sm text-gray-600 whitespace-nowrap"
        data-cy="call-transcript-match-count"
      >
        {{ matchLabel }}
      </span>
    </div>
    <div ref="pane" class="transcript p-2" data-cy="call-transcript-pane">
      <div v-for="(line, index) in highlightedLines" :key="index" class="line">
        <strong>{{ line.user }}: </strong>
        <template v-for="(part, partIndex) in line.parts" :key="partIndex">
          <mark
            v-if="part.match"
            class="bg-yellow-200 rounded-sm"
            data-cy="call-transcript-match"
            >{{ part.text }}</mark
          ><span v-else>{{ part.text }}</span>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import escapeRegExp from "lodash/escapeRegExp";
import { computed, nextTick, ref, watch } from "vue";

import { TranscriptParsedEntity } from "@/typings/call";

interface TranscriptPart {
  text: string;
  match: boolean;
}

const props = defineProps<{ lines: TranscriptParsedEntity[] }>();

const query = ref("");
const pane = ref<HTMLElement | null>(null);

const term = computed(() => query.value.trim());
const searching = computed(() => term.value.length > 0);

const splitOnTerm = (text: string): TranscriptPart[] => {
  if (!searching.value) return [{ text, match: false }];

  // The capture group keeps each match in the split result, at the odd indexes.
  return text
    .split(new RegExp(`(${escapeRegExp(term.value)})`, "i"))
    .map((piece, index) => ({ text: piece, match: index % 2 === 1 }))
    .filter((part) => part.text.length > 0);
};

const highlightedLines = computed(() =>
  props.lines.map((line) => ({
    user: line.user,
    parts: splitOnTerm(line.message ?? "")
  }))
);

const matchCount = computed(() =>
  highlightedLines.value.reduce(
    (count, line) => count + line.parts.filter((part) => part.match).length,
    0
  )
);

const matchLabel = computed(() => {
  if (matchCount.value === 0) return "No matches";
  return matchCount.value === 1 ? "1 match" : `${matchCount.value} matches`;
});

watch(term, async () => {
  if (!searching.value) return;

  await nextTick();
  pane.value
    ?.querySelector("mark")
    ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
});
</script>

<style lang="scss" scoped>
.transcript {
  background: #fff;
  box-shadow: var(--box-shadow);
  border-radius: 4px;
  overflow: auto;
  max-height: 450px;
  .line {
    margin-bottom: 12px;
    &:last-of-type {
      margin-bottom: 0;
    }
  }
}
</style>
